import React, { useState, useEffect, useCallback, useRef } from 'react';
 
const LinkedInPost = ({
  postId = "7345800068685250561",
  width = "100%",
  height = "600",
  title = "LinkedIn Post",
  showLoader = true,
  className = "",
  onLoad = () => {},
  onError = () => {}
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
 
  const embedUrl = `https://www.linkedin.com/embed/feed/update/urn:li:share:${postId}`;
 
  // Handle iframe load with timeout
  const handleIframeLoad = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsLoading(false);
    setHasError(false);
    onLoad();
  }, [onLoad]);
 
  // Handle iframe error
  const handleIframeError = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsLoading(false);
    setHasError(true);
    onError();
  }, [onError]);
 
  // Retry mechanism
  const handleRetry = useCallback(() => {
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1);
      setIsLoading(true);
      setHasError(false);
     
      // Force iframe reload
      if (iframeRef.current) {
        iframeRef.current.src = embedUrl;
      }
    }
  }, [retryCount, embedUrl]);
 
  // Reset states when postId changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setRetryCount(0);
   
    // Set timeout for loading
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setHasError(true);
    }, 15000); // 15 second timeout
 
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [postId]);
 
  return (
    <div className={`relative w-full bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">LinkedIn Post</h3>
              <p className="text-sm text-gray-400">Professional Network Content</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
              Live
            </span>
            <button
              onClick={() => window.open(`https://www.linkedin.com/feed/update/urn:li:share:${postId}`, '_blank')}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              aria-label="Open in LinkedIn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          </div>
        </div>
      </div>
 
      {/* Loading State */}
      {isLoading && showLoader && (
        <div className="flex flex-col items-center justify-center p-12 bg-gray-800"
             style={{ minHeight: height }}>
          <div className="relative">
            <div className="w-16 h-16 border-4 border-gray-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
          </div>
          <div className="mt-6 text-center">
            <h4 className="text-lg font-medium text-white mb-2">Loading LinkedIn Content</h4>
            <p className="text-sm text-gray-400">Fetching the latest updates...</p>
          </div>
        </div>
      )}
 
      {/* Error State */}
      {hasError && (
        <div className="flex flex-col items-center justify-center p-12 bg-gray-800"
             style={{ minHeight: height }}>
          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-center mb-6">
            <h4 className="text-lg font-medium text-white mb-2">Content Unavailable</h4>
            <p className="text-sm text-gray-400 mb-4">
              {retryCount >= 3 ? 'Unable to load after multiple attempts' : 'Failed to load LinkedIn post'}
            </p>
            <p className="text-xs text-gray-500">
              Post ID: {postId}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {retryCount < 3 && (
              <button
                onClick={handleRetry}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Retry ({3 - retryCount} attempts left)
              </button>
            )}
            <button
              onClick={() => window.open(`https://www.linkedin.com/feed/update/urn:li:share:${postId}`, '_blank')}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              View on LinkedIn
            </button>
          </div>
        </div>
      )}
 
      {/* LinkedIn Embed */}
      {!hasError && (
        <div className={`relative ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}>
          <iframe
            ref={iframeRef}
            src={embedUrl}
            width={width}
            height={height}
            frameBorder="0"
            allowFullScreen
            title={title}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            className="w-full border-none bg-gray-900"
            style={{ minHeight: height }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            loading="lazy"
          />
        </div>
      )}
    </div>
  );
};
 
// Enhanced Demo with Fixed Selection Logic
const LinkedInPostDemo = () => {
  const [selectedPost, setSelectedPost] = useState("7345800068685250561");
  const [savedPosts] = useState([
    {
      id: "7345800068685250561",
      name: "Where Science Meets Strategy in the Pharma Arena ",
      author: "Dr. Riyaz Syed",
      isDefault: true
    },
    {
      id: "7342852205135175682",
      name: "Rethinking Optimization Strategies in Medicinal Chemistry",
      author: "Dr. Riyaz Syed",
      isDefault: true
    },
    {
      id: "7338084750328246273",
      name: "It's a Wake-Up Call for Scientific Reasoning",
      author: "Dr. Riyaz Syed",
      isDefault: true
    },
    {
      id: "7343224421865672706",
      name: "Small Molecules, Big Impact: $31B in Q1 Revenues",
      author: "Dr. Poornachandra Y",
      isDefault: true
    },
    {
      id: "7340717590085832704",
      name: "Anaplastic Lymphoma Kinase",
      author: "Dr. Poornachandra Y",
      isDefault: true
    },
    {
      id: "7335613405866639360",
      name: "The AI-First Approach to Molecule Design",
      author: "Dr. Poornachandra Y",
      isDefault: true
    }
  ]);
 
  const handlePostLoad = useCallback(() => {
    console.log('Post loaded successfully');
  }, []);
 
  const handlePostError = useCallback(() => {
    console.log('Post failed to load');
  }, []);
 
  const handlePostSelect = (postId) => {
    setSelectedPost(postId);
  };
 
  // Find the selected post details
  const selectedPostDetails = savedPosts.find(post => post.id === selectedPost);
 
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}

 
      {/* Controls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gray-900  rounded-2xl shadow-2xl border border-gray-800 p-6 mb-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white">Recent Posts</h2>
            <p className="text-lg text-gray-200 mt-1">Click on any post to view it below</p>
          </div>
 
          {/* Post List */}
          <div className="grid grid-cols-1  md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedPosts.map(post => (
              <div
                key={post.id}
                className={`relative p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer transform hover:scale-105 ${
                  selectedPost === post.id
                    ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
                    : 'border-gray-700 hover:border-gray-600 hover:bg-gray-800'
                }`}
                onClick={() => handlePostSelect(post.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className=" text-white  truncate">{post.name}</div>
                    <div className="text-sm text-gray-400 mt-1">{post.author}</div>
                  </div>
                  {selectedPost === post.id && (
                    <div className="ml-2 flex items-center justify-center w-6 h-6 bg-blue-500 rounded-full">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
 
        {/* LinkedIn Post Embed - Only show selected post */}
        {selectedPost && (
          <div className="w-full mx-auto">
            <div className="mb-4 text-center">
              <h3 className="text-lg font-semibold text-white">
                {selectedPostDetails?.name || 'Selected Post'}
              </h3>
              <p className="text-sm text-gray-400">
                by {selectedPostDetails?.author || 'Unknown Author'}
              </p>
            </div>
            <LinkedInPost
              key={selectedPost} // Key ensures component re-renders when post changes
              postId={selectedPost}
              height="600"
              showLoader={true}
              onLoad={handlePostLoad}
              onError={handlePostError}
            />
          </div>
        )}
      </div>
    </div>
  );
};
 
export default LinkedInPostDemo;
 